import { useSearchParams } from 'react-router';

/**
 * Para onde levar o visitante depois de entrar.
 *
 * A landing mora em outro domínio (`titularis.com.br`) e manda o visitante para
 * cá com a intenção dele no endereço: quem clicou em "Comprar o Enac" chega em
 * `/entrar?destino=/planos`. Sem isto, a intenção morre no login - a pessoa
 * clicou para comprar, autenticou e caiu na vitrine de módulos, tendo que
 * procurar de novo o que já tinha escolhido.
 *
 * Vale também para quem já está logado: o `RedirectIfAuthenticated` usa o mesmo
 * destino, senão o clique em "Comprar" mandaria o assinante para a Home.
 */
const PADRAO = '/';

/**
 * Só caminho interno, nunca URL.
 *
 * `?destino=https://outro-site` transformaria a nossa tela de login num
 * trampolim: o golpista manda o link com o nosso domínio, a pessoa entra de
 * verdade e sai despejada no site dele, já confiando. É a brecha de redirecionamento
 * aberto, e a defesa é uma linha - **exigir que comece com uma barra e não com
 * duas**, porque `//outro-site` o navegador lê como URL absoluta.
 */
export function destinoSeguro(bruto: string | null): string {
  if (!bruto) return PADRAO;
  if (!bruto.startsWith('/')) return PADRAO;
  if (bruto.startsWith('//')) return PADRAO;
  // `/\` é o mesmo truque com a barra invertida, que alguns navegadores aceitam.
  if (bruto.startsWith('/\\')) return PADRAO;
  return bruto;
}

/** O destino pedido no endereço, já validado. */
export function useDestino(): string {
  const [busca] = useSearchParams();
  return destinoSeguro(busca.get('destino'));
}

/**
 * O mesmo caminho, carregando o destino adiante.
 *
 * É o que mantém a intenção viva quando a pessoa troca de tela no meio do
 * caminho - do login para o cadastro, e de volta.
 */
export function comDestino(caminho: string, destino: string): string {
  if (destino === PADRAO) return caminho;
  return `${caminho}?destino=${encodeURIComponent(destino)}`;
}

/**
 * Este destino só existe porque a pessoa clicou em comprar.
 *
 * A landing manda quem clica em "Comprar o Enac" para o cadastro com
 * `?destino=/configuracoes/plano`, e a tela que o recebia dizia apenas "Comece
 * a estudar hoje": nada lembrava que havia uma compra em curso, e o cadastro
 * parecia um desvio em vez de um passo.
 *
 * Os caminhos antigos entram porque a landing publicada ainda pode apontar para
 * eles - `/planos` e `/assinatura` redirecionam no app desde 02/09/2026, e
 * quem chega por um link velho veio comprar do mesmo jeito.
 */
export function veioComprar(destino: string): boolean {
  return ['/configuracoes/plano', '/planos', '/assinatura', '/compra'].some(
    (caminho) => destino === caminho || destino.startsWith(`${caminho}/`),
  );
}
